import { Injectable } from '@angular/core';

import { Dict } from '../../model/dict';
import { MeasurementSystem } from '../../model/enums/measurement-system';
import { USWeightMetricTranslation } from '../../model/prefixed-unit/us-weight-metric-translation';
import { WeightMetric } from '../../model/prefixed-unit/weight.metric';
import {
  ConversionData,
  CustomUnitConversion,
  customUnitConversions,
  EnergyUnit,
  EnergyUnitConversion,
  energyUnitConversionsMetricToUSCS,
  UniqueConversionUnit,
  uniqueUnitConversions,
  UnitConversion,
} from './unit-conversion.constant';

@Injectable({
  providedIn: 'root',
})
export class UnitConversionService {
  /**
   * Structure of conversions:
   * {
   *  [origin measurement system]: {
   *   [destination measurement system]: {
   *    [origin unit]: {
   *      unit,
   *      coefficient
   *    }
   *   }
   *  }
   * }
   */
  private _conversions: Dict<Dict<Dict<{ unit: string; coefficient: number }>>> = {};
  /**
   * Structure of energy conversions:
   * {
   *  [origin measurement system]: {
   *   [destination measurement system]: {
   *    [origin unit]: {
   *      [energy unit]: {
   *        unit,
   *        coefficient
   *     }
   *    }
   *   }
   *  }
   * }
   */
  private _energyConversions: Dict<Dict<Dict<Dict<{ unit: string; coefficient: number }>>>> = {};

  private readonly _CUSTOM_UNIT_CONVERSIONS: CustomUnitConversion = customUnitConversions;
  private readonly _ENERGY_UNIT_CONVERSIONS_METRIC_TO_USCS: EnergyUnitConversion = energyUnitConversionsMetricToUSCS;
  private readonly _UNIT_CONVERSIONS: UnitConversion = uniqueUnitConversions;

  constructor() {
    this._initConversions();
    this._initEnergyConversions();
  }

  transformEnergyUnit(from: MeasurementSystem, to: MeasurementSystem, unit: string, energyUnit: EnergyUnit): string {
    return this._getEnergyConversionDestinationUnit(from, to, unit, energyUnit);
  }

  transformEnergyValue(
    from: MeasurementSystem,
    to: MeasurementSystem,
    unit: string,
    energyUnit: EnergyUnit,
    value: number
  ): number {
    return value * this._getEnergyConversionCoefficient(from, to, unit, energyUnit);
  }

  /** Unit transformation for labels */
  transformLabelUnit(unit: WeightMetric, measurementSystem: MeasurementSystem) {
    return measurementSystem === MeasurementSystem.USCustomaryUnits ? USWeightMetricTranslation[unit] : unit;
  }

  transformName(from: MeasurementSystem, to: MeasurementSystem, unit: string): string {
    return this._conversions[from]?.[to]?.[unit]?.unit ?? unit;
  }

  transformValue(from: MeasurementSystem, to: MeasurementSystem, unit: string, value: number): number {
    const coefficient = this._conversions[from]?.[to]?.[unit]?.coefficient ?? 1;
    return value * coefficient;
  }

  // From metric to other measurement system, divide with denominators, and multiply with factors
  private _calculateCoefficient(
    measurementSystem: MeasurementSystem,
    factors?: UniqueConversionUnit[],
    denominators?: UniqueConversionUnit[]
  ): number {
    const nativeConversions = this._UNIT_CONVERSIONS[measurementSystem];
    let coefficient = 1;
    denominators?.forEach((dn) => (coefficient /= nativeConversions[dn].coefficient));
    factors?.forEach((f) => (coefficient *= nativeConversions[f].coefficient));
    return coefficient;
  }

  // From other measurement system to metric, divide with factors, and multiply with denominators
  private _calculateReverseCoefficient(
    measurementSystem: MeasurementSystem,
    factors?: UniqueConversionUnit[],
    denominators?: UniqueConversionUnit[]
  ): number {
    const nativeConversions = this._UNIT_CONVERSIONS[measurementSystem];
    let reverseCoefficient = 1;
    factors?.forEach((f) => (reverseCoefficient /= nativeConversions[f].coefficient));
    denominators?.forEach((dn) => (reverseCoefficient *= nativeConversions[dn].coefficient));
    return reverseCoefficient;
  }

  private _getEnergyConversionCoefficient(
    from: MeasurementSystem,
    to: MeasurementSystem,
    unit: string,
    destinationEnergyUnit: EnergyUnit
  ): number {
    return this._energyConversions[from]?.[to]?.[unit]?.[destinationEnergyUnit]?.coefficient ?? 1;
  }

  private _getEnergyConversionDestinationUnit(
    from: MeasurementSystem,
    to: MeasurementSystem,
    unit: string,
    destinationEnergyUnit: EnergyUnit
  ): string {
    return this._energyConversions[from]?.[to]?.[unit]?.[destinationEnergyUnit]?.unit ?? unit;
  }

  private _initConversions(): void {
    const conversions: Dict<Dict<Dict<{ unit: string; coefficient: number }>>> = {};
    conversions[MeasurementSystem.Metric] = { [MeasurementSystem.USCustomaryUnits]: {} };
    conversions[MeasurementSystem.USCustomaryUnits] = { [MeasurementSystem.Metric]: {} };
    Object.keys(this._CUSTOM_UNIT_CONVERSIONS).forEach((originUnit) => {
      const conversion: {
        name: string;
        factors?: UniqueConversionUnit[];
        denominators?: UniqueConversionUnit[];
      } = this._CUSTOM_UNIT_CONVERSIONS[originUnit]!;
      const coefficient = this._calculateCoefficient(
        MeasurementSystem.USCustomaryUnits,
        conversion.factors,
        conversion.denominators
      );
      conversions[MeasurementSystem.Metric][MeasurementSystem.USCustomaryUnits][originUnit] = {
        unit: conversion.name,
        coefficient,
      };
      // reverse (USCS to metric)
      const reverseCoefficient = this._calculateReverseCoefficient(
        MeasurementSystem.USCustomaryUnits,
        conversion.factors,
        conversion.denominators
      );
      conversions[MeasurementSystem.USCustomaryUnits][MeasurementSystem.Metric][conversion.name] = {
        unit: originUnit,
        coefficient: reverseCoefficient,
      };
    });
    this._conversions = conversions;
  }

  private _initEnergyConversions(): void {
    const energyConversions: Dict<Dict<Dict<Dict<{ unit: string; coefficient: number }>>>> = {};
    energyConversions[MeasurementSystem.Metric] = { [MeasurementSystem.USCustomaryUnits]: {} };
    energyConversions[MeasurementSystem.USCustomaryUnits] = { [MeasurementSystem.Metric]: {} };
    Object.keys(this._ENERGY_UNIT_CONVERSIONS_METRIC_TO_USCS).forEach((originUnit) => {
      energyConversions[MeasurementSystem.Metric][MeasurementSystem.USCustomaryUnits][originUnit] = {};
      Object.keys(this._ENERGY_UNIT_CONVERSIONS_METRIC_TO_USCS[originUnit]).forEach((energyUnit) => {
        const conversion: ConversionData =
          this._ENERGY_UNIT_CONVERSIONS_METRIC_TO_USCS[originUnit][energyUnit as EnergyUnit]!;
        const coefficient = this._calculateCoefficient(
          MeasurementSystem.USCustomaryUnits,
          conversion.factors,
          conversion.denominators
        );
        const destinationUnit = conversion.unit;
        energyConversions[MeasurementSystem.Metric][MeasurementSystem.USCustomaryUnits][originUnit][energyUnit] = {
          unit: destinationUnit,
          coefficient,
        };
        // reverse (USCS to metric)
        if (
          energyConversions[MeasurementSystem.USCustomaryUnits][MeasurementSystem.Metric][destinationUnit] === undefined
        ) {
          energyConversions[MeasurementSystem.USCustomaryUnits][MeasurementSystem.Metric][destinationUnit] = {};
        }
        const reverseCoefficient = this._calculateReverseCoefficient(
          MeasurementSystem.USCustomaryUnits,
          conversion.factors,
          conversion.denominators
        );
        energyConversions[MeasurementSystem.USCustomaryUnits][MeasurementSystem.Metric][destinationUnit] = {
          [EnergyUnit.KWH]: {
            unit: originUnit,
            coefficient: reverseCoefficient,
          },
        };
      });
    });
    this._energyConversions = energyConversions;
  }
}
